import type { CoverageDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';
import { useMemo } from 'react';
import IncludedCoverages from './IncludedCoverages';
import ExcludedCoverages from './ExcludedCoverages';
import { ListBox, Box } from '@flos/react-ui';
import { CHO_STANDARD_COVERAGE } from '../../constants/coverage';
import useWindowSize from '../../hooks/useWindowSize';
import { useRecoilValue } from 'recoil';
import { businessFlowSelector } from '../../recoil/selectors/getBusinessFlowSelector';

const Coverages = ({
    shouldRenderTitle = false,
    showCoverableDetailsUI = false,
    coverages,
    flavourName,
    coverableId,
    coverableStatus
}: {
    shouldRenderTitle?: boolean;
    showCoverableDetailsUI?: boolean;
    coverages: CoverageDTO[] | undefined;
    flavourName?: string;
    coverableId?: string;
    coverableStatus?: string;
}): React.JSX.Element => {
    const { isMobileDevice } = useWindowSize();
    const listBoxIsOpen = !isMobileDevice;
    const businessFlow = useRecoilValue(businessFlowSelector);
    const filteredCoverages = useMemo(
        () =>
            businessFlow === 'ONSA'
                ? coverages?.filter((coverage) => coverage.publicID !== 'PRHUnderExtensionCov')
                : coverages,
        [businessFlow, coverages]
    );
    const includedCoverages = useMemo(
        () => filteredCoverages?.filter((coverage) => coverage.selected),
        [filteredCoverages]
    );
    const excludedCoverages = useMemo(
        () => filteredCoverages?.filter((coverage) => !coverage.selected),
        [filteredCoverages]
    );
    const getTitleText = (flavourName: string | undefined): string => {
        if (flavourName !== 'Ejerskifteforsikring') return 'Hvad er ikke dækket?';
        const standardCoverage =
            excludedCoverages?.filter((coverage) => coverage.publicID === CHO_STANDARD_COVERAGE) ??
            [];
        return standardCoverage.length > 0 ? 'Skift dækning' : 'Udvid forsikringen';
    };
    return showCoverableDetailsUI ? (
        <div>
            <div className="u-spacing-inset-m--stretch-max">
                <ListBox title="Hvad er dækket?" isOpen>
                    <IncludedCoverages
                        showCoverableDetailsUI={showCoverableDetailsUI}
                        includedCoverages={includedCoverages}
                        shouldRenderTitle={shouldRenderTitle}
                        flavourName={flavourName ?? ''}
                        coverableId={coverableId ?? ''}
                        coverableStatus={coverableStatus ?? ''}
                    />
                </ListBox>
            </div>
            {(excludedCoverages?.length ?? 0) > 0 && (
                <div className="u-spacing-inset-m--stretch-max">
                    <ListBox title={getTitleText(flavourName)} isOpen={listBoxIsOpen}>
                        <ExcludedCoverages
                            showCoverableDetailsUI={showCoverableDetailsUI}
                            excludedCoverages={excludedCoverages}
                            shouldRenderTitle={shouldRenderTitle}
                            flavourName={flavourName ?? ''}
                            coverableId={coverableId ?? ''}
                            includedCoverages={includedCoverages}
                        />
                    </ListBox>
                </div>
            )}
        </div>
    ) : (
        <div className="u-flex-box u-flex-box--col u-gap-3 u-gap-5@md">
            {!(includedCoverages?.length === 0) && (
                <Box className="u-border-none u-shadow-none">
                    <IncludedCoverages
                        showCoverableDetailsUI={showCoverableDetailsUI}
                        includedCoverages={includedCoverages}
                        shouldRenderTitle={shouldRenderTitle}
                        flavourName={flavourName ?? ''}
                        coverableId={coverableId ?? ''}
                        coverableStatus={coverableStatus ?? ''}
                    />
                </Box>
            )}
            {!(excludedCoverages?.length === 0) && (
                <Box className="u-border-none u-shadow-none">
                    <ExcludedCoverages
                        showCoverableDetailsUI={showCoverableDetailsUI}
                        excludedCoverages={excludedCoverages}
                        shouldRenderTitle={shouldRenderTitle}
                        flavourName={flavourName ?? ''}
                        coverableId={coverableId ?? ''}
                        includedCoverages={includedCoverages}
                    />
                </Box>
            )}
        </div>
    );
};

export default Coverages;
